import {RichUtils} from 'draft-js';
import lodash from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import ReactSelect from 'react-select';

import {
  MIN_LINE_HEIGHT,
  MAX_LINE_HEIGHT,
  LINE_HEIGHT_DIVISOR,
  LINE_HEIGHT_FIXED,
  LINE_HEIGHT_PREFIX,
} from '../../constants/styleConstants';
import {addLineHeightToStyleMap, getAllActiveStylesWithPrefix, removeFromStyleMap} from '../../utils/styleHelper';

import './lineHeightSelect.scss';

/**
 * The default line height to fall back to.
 * @const {string}
 */
const DEFAULT_LINE_HEIGHT = '100';

/**
 * The list of line height values.
 *
 * @type {Array.<{label: string, value: string}>}
 */
const LINE_HEIGHT_VALUES = lodash.range(MIN_LINE_HEIGHT, MAX_LINE_HEIGHT + 1).map((lineHeight) => {
  const label = Number.parseFloat(lineHeight / LINE_HEIGHT_DIVISOR).toFixed(LINE_HEIGHT_FIXED);
  return {label: label, value: label};
});

/**
 * Converts a line height into the draftJS style name.
 *
 * @param {string} lineHeight
 * @returns {string}
 */
function convertLineHeightToStyleName(lineHeight) {
  return `${LINE_HEIGHT_PREFIX}${lineHeight}`;
}

/**
 * Gets the active line height from a list of styles.
 *
 * @param {string[]} allStyles
 * @param {string} defaultValue
 * @returns {number} The line height
 */
function getLineHeightFromStyles(allStyles, defaultValue) {
  if (!allStyles || !allStyles.forEach) {
    return defaultValue;
  }

  let foundLineHeight = defaultValue;
  allStyles.forEach((style) => {
    if (style.substr(0, LINE_HEIGHT_PREFIX.length) === LINE_HEIGHT_PREFIX) {
      foundLineHeight = style.substr(LINE_HEIGHT_PREFIX.length);
    }
  });
  return foundLineHeight;
}

/**
 * The LineHeightSelect component.
 *
 * @param {{}} params
 * @param {{}} params.editorState
 * @param {function(EditorState)} params.onChangeTextState
 * @param {function()=} params.beforeChange
 * @returns {React.Component}
 */
export const LineHeightSelect = ({editorState, onChangeTextState, beforeChange}) => {
  const currentStyle = editorState.getCurrentInlineStyle();

  const currentValue = getLineHeightFromStyles(currentStyle.toArray(), DEFAULT_LINE_HEIGHT);

  /**
   * Updates the editor state with a new style when the selection changes.
   *
   * @param {{value: string}} selected
   */
  const onChange = (selected) => {
    let changeState = editorState;
    if (beforeChange) {
      changeState = beforeChange();
    }

    const newLineHeight = selected.value;
    const newStyleName = convertLineHeightToStyleName(newLineHeight);

    const beforeChangeStyle = changeState.getCurrentInlineStyle();
    const activeLineHeights = getAllActiveStylesWithPrefix(changeState, LINE_HEIGHT_PREFIX);

    // First remove any line heights currently set.
    let newEditorState = changeState;
    lodash.forEach(activeLineHeights, (unused, activeLineHeight) => {
      if (beforeChangeStyle.has(activeLineHeight)) {
        newEditorState = RichUtils.toggleInlineStyle(newEditorState, activeLineHeight);
      } else {
        /*
         * Because of a bug in detecting active styles in mixed blocks, we need to turn on this line height everywhere,
         * and then off again. This will make sure all the currently set line heights are removed.
         */
        newEditorState = RichUtils.toggleInlineStyle(newEditorState, activeLineHeight);
        newEditorState = RichUtils.toggleInlineStyle(newEditorState, activeLineHeight);
      }

      const newActiveLineHeights = getAllActiveStylesWithPrefix(newEditorState, LINE_HEIGHT_PREFIX);
      if (!newActiveLineHeights[activeLineHeight]) {
        // In order to prevent a memory build up, remove this style if it is no longer in use.
        removeFromStyleMap(activeLineHeight);
      }
    });

    addLineHeightToStyleMap(newStyleName, newLineHeight);

    newEditorState = RichUtils.toggleInlineStyle(newEditorState, newStyleName);
    onChangeTextState(newEditorState);
  };

  return (
    <div className="line-height-select">
      <ReactSelect
        className="site-select theme-input-white"
        classNamePrefix="react-select"
        isClearable={false}
        isMulti={false}
        onChange={onChange}
        options={LINE_HEIGHT_VALUES}
        value={LINE_HEIGHT_VALUES.find((option) => option.value === currentValue)}
      />
    </div>
  );
};

LineHeightSelect.propTypes = {
  editorState: PropTypes.object.isRequired,
  onChangeTextState: PropTypes.func.isRequired,

  beforeChange: PropTypes.func,
};

export default LineHeightSelect;
